import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import 'react-datepicker/dist/react-datepicker.css';
import './dateRangePicker.css';
import { formatDateRangeLabel } from './dateRangePickerUtils';
import { BRAND_ORANGE } from '../../../styles/portalTheme';

const DateRangeTrigger = forwardRef(function DateRangeTrigger(
  { label, onClick, disabled, 'aria-label': ariaLabel },
  ref,
) {
  return (
    <Button
      ref={ref}
      type="button"
      variant="contained"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || label}
      startIcon={<CalendarTodayIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />}
      sx={{
        width: { xs: '100%', sm: 'auto' },
        minWidth: { sm: 320 },
        maxWidth: '100%',
        minHeight: { xs: 44, sm: 36.5 },
        px: { xs: 1.5, sm: 2 },
        pr: { xs: 5.25, sm: 5.75 },
        py: { xs: 1, sm: 0.75 },
        bgcolor: BRAND_ORANGE,
        color: 'white',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
        justifyContent: { xs: 'flex-start', sm: 'center' },
        '&:hover': {
          bgcolor: 'primary.dark',
        },
      }}
    >
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          flex: { xs: 1, sm: '0 1 auto' },
          textAlign: { xs: 'left', sm: 'center' },
        }}
      >
        {label}
      </Box>
    </Button>
  );
});

const CalendarWidget = ({ startDate, endDate, onDateRangeChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [localDateRange, setLocalDateRange] = React.useState([null, null]);
  const [localStartDate, localEndDate] = localDateRange;
  const selectedStartDate = startDate ?? localStartDate;
  const selectedEndDate = endDate ?? localEndDate;

  const label = formatDateRangeLabel(selectedStartDate, selectedEndDate, { compact: isMobile });

  return (
    <Box
      className="ss-date-range-picker"
      sx={{
        width: { xs: '100%', sm: 'auto' },
        maxWidth: '100%',
      }}
    >
      <DatePicker
        selectsRange
        startDate={selectedStartDate}
        endDate={selectedEndDate}
        onChange={(update) => {
          if (onDateRangeChange) {
            onDateRangeChange(update);
            return;
          }
          setLocalDateRange(update);
        }}
        customInput={<DateRangeTrigger label={label} aria-label="Select date range" />}
        isClearable
        withPortal={isMobile}
        popperClassName="ss-date-range-picker-popper"
        popperPlacement={isMobile ? 'bottom' : 'bottom-end'}
        monthsShown={1}
        showPopperArrow={!isMobile}
      />
    </Box>
  );
};

export default CalendarWidget;
